import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../components/index.js';
import { resolveAndRoute } from '../../utils/parentBridge.js';
import './Embed.css';

const log = (...args) => console.log('[postcards/embed]', ...args);
const warn = (...args) => console.warn('[postcards/embed]', ...args);

/**
 * Iframe entry point. Does the INITIAL resolution (URL params, cached usr, or a
 * usr-request to the parent) and then shows a spinner. Ongoing parent changes
 * (new campaignId, appHost, etc.) are handled by the app-wide <ParentBridge>,
 * which keeps listening after we navigate away from here.
 *
 * Routing rules (shared via resolveAndRoute):
 *  - nacCampaignId > 0  → /campaign/:id   (view an existing campaign)
 *  - nacCampaignId 0/—  → /shops/:id      (create a NEW campaign)
 */
export const Embed = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [status, setStatus] = useState('Waiting for parent data...');

  useEffect(() => {
    log('mount', { isInIframe: window.parent !== window });

    const params = new URLSearchParams(window.location.search);
    const urlCampaign =
      params.get('campaignId') || params.get('nacCampaignId') || params.get('nca');
    const urlLid = params.get('lid');

    // 1. Params in the iframe URL (campaign and/or lid). resolveAndRoute treats
    //    a 0/empty campaign as "create new" and falls back to the lid.
    if (urlCampaign != null || urlLid) {
      log('resolving from URL params', { urlCampaign, urlLid });
      resolveAndRoute(
        { nacCampaignId: urlCampaign, lid: urlLid },
        navigate,
        { onError: setError, onStatus: setStatus }
      );
      return;
    }

    // 2. Cached usr from a previous load (reload support).
    const stored = localStorage.getItem('usr');
    if (stored) {
      try {
        const usrData = JSON.parse(stored);
        log('using cached usr', { lid: usrData.lid });
        resolveAndRoute(usrData, navigate, {
          onError: setError,
          onStatus: setStatus,
        });
        return;
      } catch (err) {
        warn('failed to parse cached usr', err.message);
      }
    }

    // 3. Ask the parent. The app-wide <ParentBridge> catches the response.
    if (window.parent !== window) {
      log('requesting usr from parent');
      window.parent.postMessage({ type: 'usr-request' }, '*');
    } else {
      warn('not inside iframe — cannot request usr from parent');
      setError('This page must be loaded inside an iframe.');
    }
  }, [navigate]);

  if (error) {
    return (
      <div className="container-embed">
        <div className="error-embed">{error}</div>
      </div>
    );
  }

  return (
    <div className="container-embed">
      <LoadingSpinner size="large" message={status} />
    </div>
  );
};

export default Embed;
